import {
  HHBox,
  HHButton,
  HHPaper,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { pick, snakeCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useDynamicDialogContext } from '../../billing-tool/context/dynamic-dialog.context';
import { useSnackbarContext } from '../../billing-tool/context/snackbar.context';
import { useGetFileDetailsQuery } from '../../billing-tool/types';
import {
  ADJUSTMENT_FILE_NAME_TEMPLATE,
  BULK_ADJUSTMENTS_CATEGORY,
  getAdjustmentFileNameError,
  useAdjustmentHeaderCheck,
} from '../components/bulk-adjustments';
import CsvUploadStatus from '../components/csv-uploader-status';
import { FileCategoryRequirements } from '../components/file-category-requirements';
import { FileCategorySection } from '../components/file-category-section';
import {
  AvailableFileCategories,
  FileDetail,
} from '../components/file-upload-types';
import { UploadMethodSection } from '../components/upload-method-section';
import { appendUploadStatus } from '../components/upload-status-storage';
import { useAdjustmentUpload } from './adjustment-upload-hook';
import { useFileUpload } from './upload-hook';

export const BULK_ADJUSTMENTS_REQUIREMENTS_TEST_ID =
  'csv-upload-bulk-adjustments-requirements-text';

export interface CsvUploadPageProps {
  allowedCategories: readonly string[];
}

interface CsvUploadFormData {
  fileCategorySelect: string;
  file: File | null;
  googleSheetsUrl: string | null;
}

const CsvUploadPage = ({
  allowedCategories,
}: CsvUploadPageProps): JSX.Element => {
  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      fileCategorySelect: '',
      file: null as File | null,
      googleSheetsUrl: '',
    },
  });

  const [persistGoogleSheetsUrl, setPersistGoogleSheetsUrl] =
    useState<string>('');
  const [persistedFileName, setPersistedFileName] = useState<string>('');

  const { reset } = methods;
  const { setSnackbarState } = useSnackbarContext();
  const { setDialogContent } = useDynamicDialogContext();

  const selectedFileCategory = useWatch({
    control: methods.control,
    name: 'fileCategorySelect',
  });

  const googleSheetsUrl = useWatch({
    control: methods.control,
    name: 'googleSheetsUrl',
  });

  const uploadedFile = useWatch({
    control: methods.control,
    name: 'file',
  });

  useEffect(() => {
    if (googleSheetsUrl && googleSheetsUrl.trim() !== '' && uploadedFile) {
      methods.setValue('file', null);
    }
  }, [googleSheetsUrl, uploadedFile, methods]);

  const [
    uploadFile,
    uploadGoogleSheet,
    { loading, error, data, errorMessage },
  ] = useFileUpload();

  const { data: fileDetails } = useGetFileDetailsQuery();

  const requiredAdjustmentColumns = useMemo(
    () =>
      (
        (fileDetails?.getFileDetails?.schemas?.[BULK_ADJUSTMENTS_CATEGORY] ??
          []) as FileDetail[]
      )
        .filter(column => column.required)
        .map(column => column.columnName),
    [fileDetails],
  );

  const { headerError, isCheckingHeader } = useAdjustmentHeaderCheck(
    selectedFileCategory,
    uploadedFile,
    requiredAdjustmentColumns,
  );

  const isUploadEnabled =
    selectedFileCategory &&
    !getAdjustmentFileNameError(selectedFileCategory, uploadedFile) &&
    !headerError &&
    !isCheckingHeader &&
    (uploadedFile !== null ||
      (selectedFileCategory !== BULK_ADJUSTMENTS_CATEGORY &&
        googleSheetsUrl &&
        googleSheetsUrl.trim() !== ''));

  const { uploadAdjustmentFile, isUploading, progress } = useAdjustmentUpload();

  const onSubmit = async (data: CsvUploadFormData): Promise<void> => {
    if (data.fileCategorySelect === BULK_ADJUSTMENTS_CATEGORY && data.file) {
      const { errorMessage: uploadError } = await uploadAdjustmentFile(
        data.file,
      );

      if (uploadError) {
        setSnackbarState(uploadError, 'error', 5000);
        return;
      }

      setSnackbarState('File uploaded successfully', 'success', 4000);
      reset();

      return;
    }

    if (data.file) {
      uploadFile(data.file, snakeCase(data.fileCategorySelect));
    } else if (data.googleSheetsUrl) {
      uploadGoogleSheet(
        data.googleSheetsUrl,
        snakeCase(data.fileCategorySelect),
      );
    }

    setPersistedFileName(uploadedFile?.name || '');
    setPersistGoogleSheetsUrl(googleSheetsUrl || '');
    reset();
  };

  const availableFileDetails: AvailableFileCategories = pick(
    fileDetails?.getFileDetails?.schemas ?? {},
    allowedCategories,
  );

  useEffect(() => {
    const taskId =
      (data as { taskId?: string })?.taskId ||
      (data as { googleFileUpload?: { taskId: string } })?.googleFileUpload
        ?.taskId;

    if (taskId) {
      const isGoogleSheet = !!(
        data as { googleFileUpload?: { taskId: string } }
      )?.googleFileUpload?.taskId;

      appendUploadStatus({
        workflowId: taskId,
        fileName: isGoogleSheet ? '' : persistedFileName,
        fileLink: isGoogleSheet ? persistGoogleSheetsUrl : '',
        fileType: isGoogleSheet ? 'Google Sheet' : 'CSV File',
      });
    }
  }, [data, persistedFileName, persistGoogleSheetsUrl]);

  useEffect(() => {
    if (data && !loading) {
      const isGoogleSheet = !!(
        data as { googleFileUpload?: { taskId: string } }
      )?.googleFileUpload?.taskId;
      setSnackbarState(
        isGoogleSheet
          ? 'Google Sheets URL submitted successfully'
          : 'File uploaded successfully',
        'success',
        4000,
      );
    }

    if (error) {
      setSnackbarState(errorMessage || 'Error uploading file', 'error', 5000);
    }
  }, [data, error, errorMessage, loading, setSnackbarState]);

  return (
    <HHBox display="grid" gridTemplateColumns="2fr 1fr" gap={4}>
      <FormProvider {...methods}>
        <HHPaper
          elevation={3}
          sx={{ backgroundColor: 'background.default', padding: 4 }}
        >
          <HHStack direction="column" spacing={4}>
            <FileCategorySection availableFileDetails={availableFileDetails} />
            <UploadMethodSection
              isUploading={isUploading}
              progress={progress}
              headerError={headerError}
            />
            <HHStack direction="row" justifyContent="space-between" spacing={2}>
              <HHButton
                hhVariant="variant-bypass"
                variant="outlined"
                color="primary"
                onClick={(): void => {
                  setDialogContent({
                    title: (
                      <HHTypography hhVariant="section-title">
                        Upload Status
                      </HHTypography>
                    ),
                    content: <CsvUploadStatus />,
                    fullWidth: true,
                    width: 'md',
                    footer: (
                      <HHTypography hhVariant="muted">
                        <i>
                          * This upload data is saved in your browser local
                          storage
                        </i>
                      </HHTypography>
                    ),
                  });
                }}
              >
                Check Status
              </HHButton>
              <HHButton
                hhVariant="variant-bypass"
                variant="contained"
                color="primary"
                disabled={!isUploadEnabled || isUploading}
                onClick={(): Promise<void> => methods.handleSubmit(onSubmit)()}
              >
                Upload File
              </HHButton>
            </HHStack>
          </HHStack>
        </HHPaper>
      </FormProvider>

      <HHPaper
        elevation={2}
        sx={{ backgroundColor: 'background.paper', padding: 3 }}
      >
        <HHStack direction="column" spacing={2}>
          <HHTypography hhVariant="variant-bypass" variant="h6">
            File Requirements
          </HHTypography>
          <HHTypography
            hhVariant="variant-bypass"
            variant="body2"
            color="text.secondary"
          >
            {selectedFileCategory
              ? `Requirements for ${selectedFileCategory} files:`
              : 'Select a file category to view requirements'}
          </HHTypography>
          {selectedFileCategory === BULK_ADJUSTMENTS_CATEGORY && (
            <HHTypography
              hhVariant="variant-bypass"
              variant="body2"
              data-testid={BULK_ADJUSTMENTS_REQUIREMENTS_TEST_ID}
            >
              {`Filename template: ${ADJUSTMENT_FILE_NAME_TEMPLATE}`}
              <br />
              Example: Q3-2026_rebates.csv
              <br />
              The description can use letters, numbers, underscores, and hyphens
              only.
              <br />
              <br />
              Every column listed below is required. Column names must match
              exactly, including case. Columns can be in any order, and extra
              columns are allowed.
            </HHTypography>
          )}
          <FileCategoryRequirements
            availableFileDetails={availableFileDetails}
            selectedFileCategory={selectedFileCategory}
          />
        </HHStack>
      </HHPaper>
    </HHBox>
  );
};

export default CsvUploadPage;
